// Geography helpers for the leads directory's location picker. Company rows
// carry `city_state` like "Birmingham, AL 35203"; we parse that into a
// {city, state} pair and build a state→cities index the picker renders.

const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'Washington DC',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan',
  MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
  NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota',
  OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
  RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
  TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
  WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
}

export function stateName(code) {
  return STATE_NAMES[code] || code
}

// Fold case-variant city names together ("SAN FRANCISCO" / "san francisco" →
// "San Francisco") while leaving already mixed-case names (e.g. "McKinney") alone.
function normalizeCity(city) {
  const t = city.trim()
  if (/[a-z]/.test(t) && /[A-Z]/.test(t)) return t
  return t.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase())
}

// "Birmingham, AL 35203" → { city: 'Birmingham', state: 'AL' }. Returns null
// when the value isn't a recognizable "City, ST" shape.
export function parseCityState(cs) {
  const m = /^(.*?),\s*([A-Za-z]{2})(?:\s+\d.*)?$/.exec(String(cs || '').trim())
  if (!m) return null
  const state = m[2].toUpperCase()
  if (!STATE_NAMES[state]) return null
  const city = normalizeCity(m[1])
  if (!city) return null
  return { city, state }
}

export function cityKey(state, city) {
  return `${state}|${city}`
}

// [{ code, name, count, cities: [{ city, count, key }] }], sorted by count desc.
export function buildGeoIndex(rows) {
  const byState = new Map()
  for (const c of rows) {
    const p = parseCityState(c.city_state)
    if (!p) continue
    let s = byState.get(p.state)
    if (!s) {
      s = { code: p.state, name: stateName(p.state), count: 0, cities: new Map() }
      byState.set(p.state, s)
    }
    s.count += 1
    s.cities.set(p.city, (s.cities.get(p.city) || 0) + 1)
  }
  return [...byState.values()]
    .map((s) => ({
      code: s.code,
      name: s.name,
      count: s.count,
      cities: [...s.cities.entries()]
        .map(([city, count]) => ({ city, count, key: cityKey(s.code, city) }))
        .sort((a, b) => b.count - a.count || a.city.localeCompare(b.city)),
    }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
}

export const EMPTY_GEO = { mode: 'state', states: [], cities: [] }

// A predicate over company rows for the current geo selection. Empty selection
// (in the active mode) passes everything.
export function geoPredicate(geo) {
  const g = { ...EMPTY_GEO, ...geo }
  const stateSet = new Set(g.states)
  const citySet = new Set(g.cities)
  return (c) => {
    if (g.mode === 'city') {
      if (!citySet.size) return true
      const p = parseCityState(c.city_state)
      return p ? citySet.has(cityKey(p.state, p.city)) : false
    }
    if (!stateSet.size) return true
    const p = parseCityState(c.city_state)
    return p ? stateSet.has(p.state) : false
  }
}

export function geoCount(geo) {
  const g = { ...EMPTY_GEO, ...geo }
  return g.mode === 'city' ? g.cities.length : g.states.length
}

// Human summary for the picker button.
export function geoSummary(geo) {
  const g = { ...EMPTY_GEO, ...geo }
  if (g.mode === 'city') {
    if (!g.cities.length) return 'All cities'
    if (g.cities.length === 1) {
      const [st, city] = g.cities[0].split('|')
      return `${city}, ${st}`
    }
    return `${g.cities.length} cities`
  }
  if (!g.states.length) return 'All states'
  if (g.states.length === 1) return stateName(g.states[0])
  return `${g.states.length} states`
}
